import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {ArrowRight} from 'lucide-react'
import {Card, CardContent} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {useBlogPosts} from '@/hooks/useBlogPosts'
import {urlForImage} from '@/lib/sanity'
import {blogCategoryLabel} from '@/lib/blogCategories'
import type {BlogPostSummary} from '@/types/blog'

const LATEST_COUNT = 3

// The homepage is the one URL search engines visit often; until this section
// it linked to no article at all (#1014). The list is SSR-seeded by
// FrontendController under the same key /blog reads, so the links are in the
// initial HTML. No dates on the cards: a date formatted in the viewer's time
// zone can differ between the server and the browser and break hydration.
const PostCard = ({post}: {post: BlogPostSummary}) => {
  const {t} = useTranslation()
  const src = post.coverImage?.asset
    ? urlForImage(post.coverImage).width(800).fit('max').auto('format').url()
    : null
  return (
    <Link to={`/blog/${post.slug}`} className="group block h-full focus-ring rounded-lg">
      <Card className="h-full overflow-hidden hover:border-foreground/20 transition-colors">
        <div className="aspect-[16/9] bg-secondary overflow-hidden">
          {src ? (
            <img
              src={src}
              alt={post.coverImage?.alt ?? post.title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : null}
        </div>
        <CardContent className="p-5 flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            {post.categories.slice(0, 2).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {blogCategoryLabel(t, tag)}
              </Badge>
            ))}
          </div>
          <h3 className="font-display font-semibold text-foreground text-lg leading-snug group-hover:text-foreground/80 transition-colors">
            {post.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{post.excerpt}</p>
        </CardContent>
      </Card>
    </Link>
  )
}

const LatestPosts = () => {
  const {t} = useTranslation()
  const {data: posts} = useBlogPosts()
  const latest = (posts ?? []).slice(0, LATEST_COUNT)

  if (latest.length === 0) return null

  return (
    <section className="py-24 bg-secondary/30" aria-labelledby="latest-posts-heading">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-end justify-between gap-4 mb-10">
          <h2 id="latest-posts-heading" className="text-3xl md:text-4xl font-display font-bold text-foreground">
            {t('home.latestPosts.heading')}
          </h2>
          <Link
            to="/blog"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-foreground hover:gap-2 transition-all focus-ring rounded"
          >
            {t('home.latestPosts.viewAll')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {latest.map((post) => (
            <PostCard key={post.slug} post={post} />
          ))}
        </div>
        <div className="mt-8 sm:hidden">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-medium text-foreground focus-ring rounded">
            {t('home.latestPosts.viewAll')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  )
}

export default LatestPosts
